import { z } from 'zod';
import { agentStatusSchema } from './agents.js';
import { monitorStatusSchema } from './monitors.js';
/**
 * Клиентский WebSocket телеметрии (§8.3).
 *
 * Отдельно от туннеля агентов: там бинарный протокол с мультиплексированием
 * потоков, здесь — JSON и подписки. Общего у них только транспорт.
 */
/** Клиент → сервер. */
export const subscribeMessageSchema = z.object({
    type: z.literal('subscribe'),
    siteId: z.uuid(),
    /**
     * Не задано — все узлы площадки. Предел в 5000 совпадает с разумным
     * размером карты; лишние узлы молча отбрасываются (§8.3).
     */
    nodeIds: z.array(z.uuid()).max(5000).optional(),
});
export const unsubscribeMessageSchema = z.object({
    type: z.literal('unsubscribe'),
    siteId: z.uuid(),
});
export const pingMessageSchema = z.object({
    type: z.literal('ping'),
});
export const clientMessageSchema = z.discriminatedUnion('type', [
    subscribeMessageSchema,
    unsubscribeMessageSchema,
    pingMessageSchema,
]);
/** Состояние одного монитора в снимке. */
export const monitorStateSchema = z.object({
    monitorId: z.uuid(),
    nodeId: z.uuid(),
    /**
     * Дополнительный адрес узла; `null` — монитор самого узла (ADR 036).
     *
     * Здесь, а не отдельным запросом: снимок и дельты — единственное, откуда
     * браузер узнаёт про монитор, а замена списка адресов рождает мониторы
     * с новыми идентификаторами. Иначе карта красила бы точки по устаревшему
     * соответствию до следующего запроса схемы.
     *
     * `default(null)`, а не голый `nullable`: дельты идут через Redis, и во
     * время выкатки экземпляр прежней версии публикует сообщение без этого
     * поля. Обязательное поле уронило бы разбор у всех подписчиков сразу.
     */
    addressId: z.uuid().nullable().default(null),
    status: monitorStatusSchema,
    rttMs: z.number().nullable(),
    since: z.iso.datetime().nullable(),
});
/** Сервер → клиент: полный снимок сразу после подписки. */
export const snapshotMessageSchema = z.object({
    type: z.literal('snapshot'),
    siteId: z.uuid(),
    monitors: z.array(monitorStateSchema),
});
/** Одно изменение статуса. */
export const statusChangeSchema = z.object({
    monitorId: z.uuid(),
    nodeId: z.uuid(),
    /** Дополнительный адрес узла; `null` — монитор самого узла. См. `monitorStateSchema`. */
    addressId: z.uuid().nullable().default(null),
    status: monitorStatusSchema,
    rttMs: z.number().nullable(),
    error: z.string().nullable(),
    at: z.iso.datetime(),
});
/**
 * Сервер → клиент: дельта. Отправляется не чаще раза в 500 мс (§8.3),
 * поэтому в одном сообщении может быть много изменений.
 */
export const statusMessageSchema = z.object({
    type: z.literal('status'),
    changes: z.array(statusChangeSchema),
});
/** Сервер → клиент: состояние агента площадки. */
export const agentStateMessageSchema = z.object({
    type: z.literal('agent'),
    agentId: z.uuid(),
    status: agentStatusSchema,
});
export const pongMessageSchema = z.object({
    type: z.literal('pong'),
});
/**
 * Сервер → клиент: отказ. В §8.3 не описан, но подписка на чужую площадку
 * должна получать внятный ответ, а не молчание.
 */
export const wsErrorMessageSchema = z.object({
    type: z.literal('error'),
    code: z.string(),
    message: z.string(),
});
export const serverTelemetryMessageSchema = z.discriminatedUnion('type', [
    snapshotMessageSchema,
    statusMessageSchema,
    agentStateMessageSchema,
    pongMessageSchema,
    wsErrorMessageSchema,
]);
/** Путь клиентского WebSocket (§8.3). */
export const TELEMETRY_WS_PATH = '/api/v1/ws';
/** Дельты накапливаются и уходят пачкой не чаще этого (§8.3). */
export const TELEMETRY_BATCH_MS = 500;
/** Клиент, не подтвердивший ping за это время, отключается (§8.3). */
export const TELEMETRY_PING_TIMEOUT_MS = 30_000;
/** Канал Redis, в который публикуются изменения статусов площадки (§11). */
export function siteStatusChannel(siteId) {
    return `site:${siteId}:status`;
}
//# sourceMappingURL=telemetry.js.map